import * as React from 'react';
import { Button, makeStyles, MessageBar, MessageBarGroup, shorthands, tokens } from '@fluentui/react-components';
import type { History } from 'history';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { trackException } from '../modules/telemetry';
import { AuthError } from '../store/errors';

function createErrorHandler(history: History) {
  return function errorHandler(error: Error) {
    if (error instanceof AuthError) {
      history.push('/login');
      return;
    }

    trackException(error);
  };
}

function MessageErrorFallback() {
  const intl = useIntl();
  return (
    <MessageBarGroup animate="both">
      <MessageBar intent="error" role="alert">
        {intl.formatMessage({
          defaultMessage: 'We encountered an unexpected error. Please try again.',
          description: 'Generic error message',
        })}
      </MessageBar>
    </MessageBarGroup>
  );
}

function FullErrorFallback() {
  const intl = useIntl();
  const classes = useStyles();

  function handleRefresh() {
    window.location.reload();
  }

  return (
    <div className={classes.root}>
      <MessageErrorFallback />
      <div className={classes.buttons}>
        <Button appearance="primary" onClick={handleRefresh}>
          {intl.formatMessage({ defaultMessage: 'Retry', description: 'Action button to retry after an error' })}
        </Button>
      </div>
    </div>
  );
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding(tokens.spacingHorizontalL),
    ...shorthands.gap(tokens.spacingHorizontalL),
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
});

interface Props {
  children: React.ReactNode;
  type?: 'bar-only' | 'full';
}

export function ErrorBoundary({ children, type = 'bar-only' }: Props) {
  const history = useHistory();
  const FallbackComponent = type === 'bar-only' ? MessageErrorFallback : FullErrorFallback;
  return (
    <ReactErrorBoundary FallbackComponent={FallbackComponent} onError={createErrorHandler(history)}>
      {children}
    </ReactErrorBoundary>
  );
}
